import { useState } from 'react'
import { useLanguage } from '../contexts/LanguageContext'
import { translations } from '../data/translations'
import { motion } from 'framer-motion'

const Contact = () => {
  const { language } = useLanguage()
  const t = translations[language]
  const [enquiry, setEnquiry] = useState({
    Name: '',
    Email: '',
    MobileNo: '',
    Subject: '',
    Description: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target
    setEnquiry((prev) => ({
      ...prev,
      [name]: value,
    }))
    // Clear error when user starts typing
    if (error) setError(null)
    if (success) setSuccess(false)
  }

  const validateForm = () => {
    if (!enquiry.Name || enquiry.Name.trim() === '') {
      setError(t.contact.errorName)
      return false
    }
    if (!enquiry.MobileNo || enquiry.MobileNo.trim() === '') {
      setError(t.contact.errorMobile)
      return false
    }
    if (!enquiry.Email || enquiry.Email.trim() === '') {
      setError(t.contact.errorEmail)
      return false
    }
    if (!enquiry.Subject || enquiry.Subject.trim() === '') {
      setError(t.contact.errorSubject)
      return false
    }
    if (!enquiry.Description || enquiry.Description.trim() === '') {
      setError(t.contact.errorMessage)
      return false
    }
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    setError(null)
    setSuccess(false)

    try {
      const response = await fetch('https://api-smart.ainsoftdelivery.com/SaveEnquiry', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(enquiry),
      })

      if (!response.ok) {
        throw new Error('Failed to send enquiry')
      }

      const data = await response.json()

      if (data) {
        setSuccess(true)
        setError(null)
        // Reset form
        setEnquiry({
          Name: '',
          Email: '',
          MobileNo: '',
          Subject: '',
          Description: '',
        })
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccess(false)
        }, 5000)
      } else {
        setError(t.contact.errorFailed)
        setSuccess(false)
      }
    } catch (err) {
      setError(t.contact.errorFailed)
      setSuccess(false)
      console.error('Enquiry error:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const contactInfo = [
    {
      icon: '📞',
      title: language === 'en' ? 'Phone' : 'الهاتف',
      details: ['+971 50 277 2521', '050 277 2521'],
      link: 'tel:+971502772521',
      color: 'from-blue-500 to-blue-600',
    },
    {
      icon: '📧',
      title: language === 'en' ? 'Email' : 'البريد الإلكتروني',
      details: ['Info@smartmile.ae'],
      link: 'mailto:Info@smartmile.ae',
      color: 'from-purple-500 to-purple-600',
    },
    {
      icon: '📍',
      title: language === 'en' ? 'Address' : 'العنوان',
      details: [
        language === 'en'
          ? 'International City - Greek Cluster'
          : 'المدينة الدولية - مجمع اليونان',
        language === 'en'
          ? 'Building K03, Shop Number 5'
          : 'مبنى K03، متجر رقم 5',
      ],
      link: 'https://maps.app.goo.gl/t9mympo5UQYGhjgC7',
      color: 'from-green-500 to-green-600',
    },
  ]

  const socialMedia = [
    {
      name: 'Instagram',
      icon: '📷',
      url: 'https://www.instagram.com/smartmile.ae',
      color: 'from-pink-500 to-pink-600',
    },
    {
      name: 'Facebook',
      icon: '👥',
      url: 'https://www.facebook.com/smartmile.ae/',
      color: 'from-blue-600 to-blue-700',
    },
    {
      name: 'TikTok',
      icon: '🎵',
      url: 'https://www.tiktok.com/@smartmile_ae',
      color: 'from-gray-900 to-gray-800',
    },
  ]

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-primary-500 py-8 md:py-12 lg:py-16 overflow-hidden">
        {/* Grid Pattern Background */}
        <div className="absolute inset-0 z-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              {t.contact.title}
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto">
              {t.contact.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
        {/* Decorative Background */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary-500/5 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Get in Touch Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {language === 'en' ? 'Get in Touch' : 'تواصل معنا'}
              </h2>
              <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                {language === 'en'
                  ? 'We are here to help! Reach out to us through any of the following channels.'
                  : 'نحن هنا للمساعدة! تواصل معنا من خلال أي من القنوات التالية.'}
              </p>
            </div>

            {/* Contact Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {contactInfo.map((info, index) => (
                <motion.a
                  key={index}
                  href={info.link}
                  target={info.link.startsWith('http') ? '_blank' : '_self'}
                  rel={info.link.startsWith('http') ? 'noopener noreferrer' : ''}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all border-2 border-transparent hover:border-primary-300 overflow-hidden"
                >
                  {/* Background Gradient */}
                  <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${info.color} opacity-10 rounded-bl-full group-hover:opacity-20 transition-opacity`}></div>
                  
                  <div className="relative z-10">
                    <div className={`w-16 h-16 bg-gradient-to-br ${info.color} rounded-xl flex items-center justify-center shadow-lg mb-4 group-hover:scale-110 transition-transform`}>
                      <span className="text-3xl">{info.icon}</span>
                    </div>
                    <h3 className="font-bold text-gray-900 mb-3 text-xl">{info.title}</h3>
                    {info.details.map((detail, idx) => (
                      <p key={idx} className="text-gray-700 text-sm md:text-base mb-1">
                        {detail}
                      </p>
                    ))}
                  </div>
                </motion.a>
              ))}
            </div>

          </motion.div>

          {/* Send us a Message and Business Hours Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Send us a Message Section */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative bg-white/10 backdrop-blur-md rounded-3xl shadow-2xl p-8 md:p-12 border border-white/20">
                {/* Liquid Glass Overlay */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-white/0 via-white/10 to-white/0 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                
                <div className="relative z-10">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                      {language === 'en' ? 'Send us a Message' : 'أرسل لنا رسالة'}
                    </h2>
                    <p className="text-gray-700">
                      {language === 'en'
                        ? 'Fill out the form below and we\'ll get back to you as soon as possible.'
                        : 'املأ النموذج أدناه وسنعود إليك في أقرب وقت ممكن.'}
                    </p>
                  </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="Name"
                      className="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      {t.contact.name}
                    </label>
                    <input
                      type="text"
                      id="Name"
                      name="Name"
                      value={enquiry.Name}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className="w-full px-5 py-4 bg-white/10 backdrop-blur-md border border-gray-300 rounded-xl focus:outline-none focus:border-primary-500 focus:bg-white/20 transition-all placeholder-gray-400 text-gray-900"
                      placeholder={language === 'en' ? 'Your full name' : 'اسمك الكامل'}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="Email"
                      className="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      {t.contact.email}
                    </label>
                    <input
                      type="email"
                      id="Email"
                      name="Email"
                      value={enquiry.Email}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className="w-full px-5 py-4 bg-white/10 backdrop-blur-md border border-gray-300 rounded-xl focus:outline-none focus:border-primary-500 focus:bg-white/20 transition-all placeholder-gray-400 text-gray-900"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="MobileNo"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    {t.contact.phone}
                  </label>
                  <input
                    type="tel"
                    id="MobileNo"
                    name="MobileNo"
                    value={enquiry.MobileNo}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    className="w-full px-5 py-4 bg-white/10 backdrop-blur-md border border-gray-300 rounded-xl focus:outline-none focus:border-primary-500 focus:bg-white/20 transition-all placeholder-gray-400 text-gray-900"
                    placeholder="+971 XX XXX XXXX"
                  />
                </div>
                <div>
                  <label
                    htmlFor="Subject"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    {language === 'en' ? 'Subject' : 'الموضوع'}
                  </label>
                  <input
                    type="text"
                    id="Subject"
                    name="Subject"
                    value={enquiry.Subject}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    className="w-full px-5 py-4 bg-white/10 backdrop-blur-md border border-gray-300 rounded-xl focus:outline-none focus:border-primary-500 focus:bg-white/20 transition-all placeholder-gray-400 text-gray-900"
                    placeholder={language === 'en' ? 'Enter subject' : 'أدخل الموضوع'}
                  />
                </div>
                <div>
                  <label
                    htmlFor="Description"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    {t.contact.message}
                  </label>
                  <textarea
                    id="Description"
                    name="Description"
                    value={enquiry.Description}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    rows="6"
                    className="w-full px-5 py-4 bg-white/10 backdrop-blur-md border border-gray-300 rounded-xl focus:outline-none focus:border-primary-500 focus:bg-white/20 resize-none transition-all placeholder-gray-400 text-gray-900"
                    placeholder={language === 'en' ? 'Tell us about your delivery needs or question...' : 'أخبرنا عن احتياجات التوصيل الخاصة بك أو سؤالك...'}
                  ></textarea>
                </div>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-100 border-2 border-red-400 text-red-700 px-6 py-4 rounded-xl flex items-center gap-3"
                  >
                    <span className="text-2xl">⚠</span>
                    <span>{error}</span>
                  </motion.div>
                )}
                {success && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-green-100 border-2 border-green-400 text-green-700 px-6 py-4 rounded-xl flex items-center gap-3"
                  >
                    <span className="text-2xl">✓</span>
                    <span>{t.contact.successMessage}</span>
                  </motion.div>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl font-bold text-lg hover:from-primary-700 hover:to-primary-800 transition-all shadow-xl hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full"></span>
                      <span>{language === 'en' ? 'Sending...' : 'جاري الإرسال...'}</span>
                    </>
                  ) : (
                    <>
                      <span>{language === 'en' ? 'Submit' : 'إرسال'}</span>
                      <span>→</span>
                    </>
                  )}
                </button>
              </form>
                </div>
              </div>
            </motion.div>

            {/* Business Hours */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-3xl p-8 md:p-12 text-white shadow-2xl h-full">
                <h3 className="font-bold text-2xl md:text-3xl mb-6 flex items-center gap-3">
                  <span>🕐</span>
                  <span>{language === 'en' ? 'Business Hours' : 'ساعات العمل'}</span>
                </h3>
                <div className="space-y-4 text-lg">
                  <div className="flex justify-between items-center bg-white/10 backdrop-blur-sm rounded-xl px-5 py-4">
                    <span>{language === 'en' ? 'Monday - Friday' : 'الاثنين - الجمعة'}</span>
                    <span className="font-bold">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center bg-white/10 backdrop-blur-sm rounded-xl px-5 py-4">
                    <span>{language === 'en' ? 'Saturday' : 'السبت'}</span>
                    <span className="font-bold">9:00 AM - 2:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center bg-white/10 backdrop-blur-sm rounded-xl px-5 py-4">
                    <span>{language === 'en' ? 'Sunday' : 'الأحد'}</span>
                    <span className="font-bold opacity-75">
                      {language === 'en' ? 'Closed' : 'مغلق'}
                    </span>
                  </div>
                  <div className="mt-8 pt-6 border-t border-white/20">
                    <p className="font-bold text-xl text-center mb-6">
                      {language === 'en' ? '24/7 Support Available' : 'دعم متاح على مدار الساعة'}
                    </p>
                    
                    {/* Social Media */}
                    <div>
                      <h4 className="font-bold text-lg mb-4 text-center">
                        {language === 'en' ? 'Follow Us on Social Media' : 'تابعنا على وسائل التواصل الاجتماعي'}
                      </h4>
                      <div className="flex justify-center gap-4">
                        {/* Instagram */}
                        <motion.a
                          href={socialMedia[0].url}
                          target="_blank"
                          rel="noopener noreferrer"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                          className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center hover:bg-white/30 transition-all"
                          title="Instagram"
                        >
                          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                          </svg>
                        </motion.a>
                        
                        {/* Facebook */}
                        <motion.a
                          href={socialMedia[1].url}
                          target="_blank"
                          rel="noopener noreferrer"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                          className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center hover:bg-white/30 transition-all"
                          title="Facebook"
                        >
                          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                          </svg>
                        </motion.a>
                        
                        {/* TikTok */}
                        <motion.a
                          href={socialMedia[2].url}
                          target="_blank"
                          rel="noopener noreferrer"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                          className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center hover:bg-white/30 transition-all"
                          title="TikTok"
                        >
                          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                          </svg>
                        </motion.a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {language === 'en' ? 'Visit Our Office' : 'زر مكتبنا'}
            </h2>
            <p className="text-gray-700 text-lg">
              {language === 'en'
                ? 'International City - Greek Cluster, Building K03, Shop Number 5'
                : 'المدينة الدولية - مجمع اليونان، مبنى K03، متجر رقم 5'}
            </p>
          </motion.div>
          <div className="rounded-2xl overflow-hidden shadow-2xl border-2 border-gray-200">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3610.1234567890123!2d55.12345678901234!3d25.12345678901234!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjXCsDA3JzI0LjQiTiA1NcKwMDcnMjQuNCJF!5e0!3m2!1sen!2sae!4v1234567890123!5m2!1sen!2sae"
              width="100%"
              height="500"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Smart Mile Location"
            ></iframe>
            <div className="bg-white p-6 border-t border-gray-200">
              <a
                href="https://maps.app.goo.gl/t9mympo5UQYGhjgC7"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all shadow-lg hover:shadow-xl"
              >
                <span>📍</span>
                <span>{language === 'en' ? 'Open in Google Maps' : 'افتح في خرائط Google'}</span>
                <span>→</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Contact
